import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Play, CheckCircle, Clock, Users, Award, FileText, Target, Star, GraduationCap, Briefcase, Heart, Stethoscope, Baby, Shield, Eye, MessageCircle, Sparkles, Building, UserCheck, ClipboardList } from 'lucide-react';

// Couleurs pour le DEAP - thème médical/soins
const theme = {
  primary: '#1E6F8C',
  secondary: '#E8A87C',
  accent1: '#5BA4A4',
  accent2: '#C9A7C7',
  accent3: '#85C1E9',
  accent4: '#82C9A5',
  accent5: '#F5B041',
  background: '#F5F9FB',
  text: '#2C3E50',
  success: '#27AE60',
  error: '#E74C3C',
};

const slides = [
  {
    id: 1,
    title: "Réussir sa VAE DEAP",
    subtitle: "Diplôme d'État d'Auxiliaire de Puériculture",
    description: "Les étapes clés pour réussir votre validation",
    type: "title",
  },
  {
    id: 2,
    title: "Pourquoi choisir la VAE DEAP ?",
    type: "benefits",
    items: [
      "Valoriser votre expérience en secteur sanitaire et médico-social",
      "Obtenir un diplôme d'État reconnu",
      "Évoluer professionnellement",
      "Sécuriser votre parcours",
      "Accéder à des postes qualifiés"
    ]
  },
  {
    id: 3,
    title: "Conditions d'éligibilité",
    type: "eligibility",
    items: [
      "1 an d'expérience conseillé (1607h cumulées)",
      "Activités en lien avec le référentiel DEAP",
      "Expérience salariée, bénévole ou indépendante",
      "Activités en structure sanitaire, médico-sociale ou à domicile"
    ]
  },
  {
    id: 4,
    title: "Les 7 étapes de la VAE",
    type: "steps",
    items: [
      { num: 1, text: "Vérifier son éligibilité" },
      { num: 2, text: "Déposer le Livret 1" },
      { num: 3, text: "Rédiger le Livret 2" },
      { num: 4, text: "Se faire accompagner" },
      { num: 5, text: "Déposer le dossier" },
      { num: 6, text: "Passer l'oral" },
      { num: 7, text: "Décision du jury" }
    ]
  },
  {
    id: 5,
    title: "Le Livret 1",
    type: "livret1",
    items: [
      "Dossier de recevabilité",
      "Justificatifs d'expérience",
      "Ou attestations employeurs",
      "Délai de réponse : environ 2 mois parfois plus"
    ]
  },
  {
    id: 6,
    title: "Le Livret 2 : le cœur de la VAE",
    type: "livret2",
    items: [
      "Description détaillée de situations de soins",
      "Analyse de la pratique professionnelle",
      "Démonstration des compétences du référentiel 2021",
      "Posture soignante argumentée attendue"
    ]
  },
  {
    id: 7,
    title: "Les 5 blocs de compétences",
    type: "blocs-overview",
    blocs: [
      { num: 1, title: "Accompagnement et soins de l'enfant", color: theme.primary, activities: "1, 1bis, 2" },
      { num: 2, title: "Évaluation de l'état clinique", color: theme.accent1, activities: "3, 4, 5" },
      { num: 3, title: "Information et accompagnement", color: theme.accent2, activities: "6, 7" },
      { num: 4, title: "Entretien de l'environnement", color: theme.secondary, activities: "8, 9" },
      { num: 5, title: "Travail en équipe pluri-professionnelle", color: theme.accent3, activities: "10, 11" }
    ]
  },
  // BLOC 1
  {
    id: 8,
    title: "Bloc 1 – Accompagnement et soins de l'enfant",
    subtitle: "dans les activités de la vie quotidienne et de la vie sociale",
    type: "bloc-title",
    color: theme.primary,
    icon: "baby"
  },
  {
    id: 9,
    title: "Activité 1 – Accompagner l'enfant et son entourage",
    subtitle: "dans les actes essentiels de la vie quotidienne et sociale",
    type: "activity",
    color: theme.primary,
    items: [
      "Rechercher et analyser les informations sur les habitudes de vie, la culture, les choix de l'enfant",
      "Évaluer l'autonomie, les fragilités, les capacités psychomotrices et psycho-affectives",
      "Identifier et évaluer les besoins en tenant compte de la situation singulière",
      "Mettre en œuvre l'accompagnement personnalisé dans les actes essentiels",
      "Éducation à la santé et promotion de la santé auprès de l'enfant et son entourage"
    ]
  },
  {
    id: 10,
    title: "Activité 1 – Suite",
    type: "activity",
    color: theme.primary,
    items: [
      "Mobiliser les ressources de l'enfant et adapter l'accompagnement pour développer ses apprentissages",
      "Adapter la communication avec l'enfant, l'entourage et les professionnels",
      "Repérer les interactions de l'enfant avec son entourage",
      "Associer, accompagner et conforter les parents dans leur rôle"
    ]
  },
  {
    id: 11,
    title: "Activité 1bis – Activités d'éveil et de loisirs",
    subtitle: "Élaborer et mettre en œuvre des activités adaptées à l'enfant ou au groupe",
    type: "activity",
    color: theme.primary,
    items: [
      "Analyser les besoins, potentiels et centres d'intérêts de l'enfant ou du groupe",
      "Identifier les activités d'éveil, de loisirs, d'éducation adaptées",
      "Planifier, prioriser et organiser les activités",
      "Aménager l'espace dans le respect des règles d'hygiène et de sécurité",
      "Proposer et faciliter la participation de l'enfant",
      "Ajuster les activités en fonction des réactions et apprentissages"
    ]
  },
  {
    id: 12,
    title: "Activité 2 – Prévention des risques",
    subtitle: "Identifier les situations à risque et mettre en œuvre les actions de prévention",
    type: "activity",
    color: theme.primary,
    items: [
      "Identifier les signes de fragilités ou vulnérabilités potentielles",
      "Identifier les situations à risque, en particulier de maltraitance, et alerter",
      "Proposer des mesures de prévention en coopération pluri-professionnelle",
      "Évaluer les mesures mises en œuvre et réajuster"
    ]
  },
  // BLOC 2
  {
    id: 13,
    title: "Bloc 2 – Évaluation de l'état clinique",
    subtitle: "et mise en œuvre de soins adaptés en collaboration",
    type: "bloc-title",
    color: theme.accent1,
    icon: "stethoscope"
  },
  {
    id: 14,
    title: "Activité 3 – Évaluer l'état clinique",
    subtitle: "d'une personne à tout âge de la vie pour adapter sa prise en soins",
    type: "activity",
    color: theme.accent1,
    items: [
      "Rechercher et analyser les informations pour identifier l'état général",
      "Identifier les signes de changements de l'état clinique",
      "Évaluer la douleur, mesurer les paramètres vitaux",
      "Évaluer l'état clinique par un raisonnement clinique",
      "Transmettre à l'oral et à l'écrit les informations utiles",
      "Discerner le caractère urgent d'une situation et alerter"
    ]
  },
  {
    id: 15,
    title: "Activité 4 – Mettre en œuvre des soins adaptés",
    subtitle: "à l'état clinique de l'enfant",
    type: "activity",
    color: theme.accent1,
    items: [
      "Rechercher les précautions particulières à respecter",
      "Analyser et prioriser les activités de soin",
      "Appliquer les règles de bonnes pratiques",
      "Organiser et adapter le soin en fonction de l'état clinique",
      "Mettre en œuvre des soins personnalisés en collaboration avec l'IDE",
      "Expliquer le soin réalisé de manière adaptée",
      "Évaluer la qualité du soin et réajuster"
    ]
  },
  {
    id: 16,
    title: "Activité 5 – Installation et déplacements",
    subtitle: "Accompagner la personne en mobilisant ses ressources",
    type: "activity",
    color: theme.accent1,
    items: [
      "Évaluer les capacités de déplacement de la personne",
      "Effectuer une guidance verbale et non verbale",
      "Aménager un espace sécurisé et sécurisant",
      "Utiliser les dispositifs d'aide à la mobilisation",
      "Identifier les situations à risque et alerter",
      "Installer la personne en tenant compte de la singularité"
    ]
  },
  // BLOC 3
  {
    id: 17,
    title: "Bloc 3 – Information et accompagnement",
    subtitle: "des personnes, de leur entourage, des professionnels et des apprenants",
    type: "bloc-title",
    color: theme.accent2,
    icon: "message"
  },
  {
    id: 18,
    title: "Activité 6 – Communication adaptée",
    subtitle: "pour informer et accompagner la personne et son entourage",
    type: "activity",
    color: theme.accent2,
    items: [
      "Écouter la personne et son entourage (communication verbale et non verbale)",
      "Mettre en œuvre des soins relationnels adaptés à des publics diversifiés",
      "Identifier et transmettre les informations pertinentes",
      "Apporter des conseils en éducation à la santé et sécurité domestique",
      "Permettre à la personne d'exprimer ses besoins et attentes",
      "Apporter des informations pratiques lors de l'accueil"
    ]
  },
  {
    id: 19,
    title: "Activité 7 – Former les pairs et apprenants",
    subtitle: "Informer et former les personnes en formation et autres professionnels",
    type: "activity",
    color: theme.accent2,
    items: [
      "Identifier les informations à apporter lors de l'accueil",
      "Rechercher les besoins d'apprentissage",
      "Accompagner le développement des compétences",
      "Identifier les acquis et les écarts, adapter l'encadrement",
      "Évaluer les compétences avec les outils de suivi",
      "Accompagner la réflexion sur les apprentissages"
    ]
  },
  // BLOC 4
  {
    id: 20,
    title: "Bloc 4 – Entretien de l'environnement",
    subtitle: "immédiat de la personne et des matériels liés aux activités",
    type: "bloc-title",
    color: theme.secondary,
    icon: "shield"
  },
  {
    id: 21,
    title: "Activité 8 – Techniques d'entretien",
    subtitle: "des locaux et du matériel avec prévention des risques",
    type: "activity",
    color: theme.secondary,
    items: [
      "Identifier et choisir le matériel et produits appropriés",
      "Utiliser les techniques de nettoyage et désinfection",
      "Appliquer les principes d'hygiène pour prévenir les infections",
      "Respecter les circuits (linge, matériels, déchets)",
      "Respecter les circuits liés à l'alimentation",
      "Adapter ses actions en fonction du lieu d'intervention"
    ]
  },
  {
    id: 22,
    title: "Activité 9 – Anomalies et dysfonctionnements",
    subtitle: "Repérer et traiter les anomalies liées à l'entretien",
    type: "activity",
    color: theme.secondary,
    items: [
      "Évaluer l'efficacité des opérations et réajuster",
      "Repérer les anomalies et alerter",
      "Vérifier le bon fonctionnement des matériels de soins"
    ]
  },
  // BLOC 5
  {
    id: 23,
    title: "Bloc 5 – Travail en équipe pluri-professionnelle",
    subtitle: "et traitement des informations liées aux soins, qualité et gestion des risques",
    type: "bloc-title",
    color: theme.accent3,
    icon: "users"
  },
  {
    id: 24,
    title: "Activité 10 – Traitement des informations",
    subtitle: "Rechercher, traiter et transmettre les données pour la continuité des soins",
    type: "activity",
    color: theme.accent3,
    items: [
      "Rechercher, organiser et hiérarchiser les informations nécessaires",
      "Transcrire les données recueillies",
      "Transmettre par les modalités de communication appropriées",
      "Analyser les situations et relayer les informations pertinentes"
    ]
  },
  {
    id: 25,
    title: "Activité 11 – Organisation et coopération",
    subtitle: "au sein d'une équipe pluri-professionnelle",
    type: "activity",
    color: theme.accent3,
    items: [
      "Identifier son propre champ d'intervention",
      "Organiser son activité au sein de l'équipe",
      "Prioriser pour répondre aux besoins dans un contexte changeant",
      "Contribuer au projet de soins et projet de vie",
      "Signaler et déclarer les événements indésirables",
      "Contribuer à la démarche qualité et gestion des risques",
      "Évaluer sa pratique et identifier ses besoins en formation"
    ]
  },
  // CONSEILS ET ORAL
  {
    id: 26,
    title: "Conseils pour réussir",
    type: "tips",
    tips: [
      "Décrire des situations réelles",
      "Analyser ses pratiques",
      "Justifier ses choix professionnels",
      "Montrer sa réflexion soignante"
    ],
    example: {
      title: "Exemple de formulation attendue",
      wrong: "« Je fais la toilette d'un enfant »",
      right: "« Je réalise la toilette d'un enfant hospitalisé en respectant les règles d'hygiène et en surveillant son état clinique. »"
    }
  },
  {
    id: 27,
    title: "Pourquoi se faire accompagner ?",
    type: "accompagnement",
    items: [
      "Structurer ses écrits",
      "Comprendre les attentes du jury",
      "Préparer efficacement l'oral",
      "Augmenter les chances de validation totale"
    ]
  },
  {
    id: 28,
    title: "L'oral devant le jury",
    type: "oral",
    details: [
      { label: "Durée totale", value: "1 heure" },
      { label: "Présentation personnelle", value: "10 minutes" },
      { label: "Échanges avec le jury", value: "50 minutes" }
    ],
    note: "Questions sur les situations décrites et sur le référentiel et tout ce que vous leur dites."
  },
  {
    id: 29,
    title: "Décision du jury",
    type: "decision",
    decisions: [
      { status: "success", text: "Validation totale", desc: "Vous obtenez votre diplôme !" },
      { status: "partial", text: "Validation partielle", desc: "Blocs à compléter" },
      { status: "retry", text: "Refus", desc: "Possibilité de représenter l'oral en redéposant un livret 2" }
    ]
  },
  {
    id: 30,
    title: "Planning indicatif",
    type: "timeline",
    phases: [
      { phase: "Livret 1", duration: "2 mois" },
      { phase: "Livret 2", duration: "6 à 12 mois" },
      { phase: "Jury", duration: "1 à 3 mois" }
    ],
    total: "Durée totale : 6 à 12 mois"
  },
  {
    id: 31,
    title: "Débouchés professionnels",
    type: "careers",
    jobs: [
      "Service de pédiatrie",
      "Crèches",
      "Maternité",
      "Néonatalogie",
      "PMI",
      "Structures médico-sociales"
    ]
  },
  {
    id: 32,
    title: "En résumé",
    type: "summary",
    points: [
      "La VAE DEAP est exigeante et professionnalisante",
      "Elle nécessite rigueur et analyse",
      "Une préparation méthodique favorise la réussite"
    ]
  }
];

const getIcon = (iconName, size = 50) => {
  switch (iconName) {
    case 'baby': return <Baby size={size} />;
    case 'stethoscope': return <Stethoscope size={size} />;
    case 'message': return <MessageCircle size={size} />;
    case 'shield': return <Shield size={size} />;
    case 'users': return <Users size={size} />;
    default: return <Award size={size} />;
  }
};

const SlideContent = ({ slide }) => {
  switch (slide.type) {
    case "title":
      return (
        <div style={{ 
          textAlign: 'center', 
          padding: '3rem 2rem',
          background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.accent1} 100%)`,
          borderRadius: '1rem',
          color: 'white'
        }}>
          <div style={{ 
            width: '100px', 
            height: '100px', 
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 2rem',
            border: '3px solid rgba(255,255,255,0.3)'
          }}>
            <Stethoscope size={50} style={{ color: 'white' }} />
          </div>
          <h2 style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '0.5rem', color: 'white' }}>
            {slide.title}
          </h2>
          <p style={{ fontSize: '1.25rem', marginBottom: '0.5rem', opacity: 0.95 }}>{slide.subtitle}</p>
          <p style={{ fontSize: '1.1rem', opacity: 0.85 }}>{slide.description}</p>
        </div>
      );

    case "benefits":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '650px', margin: '0 auto' }}>
            {slide.items.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem',
                background: 'white', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                borderLeft: `4px solid ${theme.secondary}`
              }}>
                <Star size={20} style={{ color: theme.secondary, flexShrink: 0 }} />
                <span style={{ fontSize: '1rem', color: theme.text }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "eligibility":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '600px', margin: '0 auto' }}>
            {slide.items.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem',
                background: 'white', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                borderLeft: `4px solid ${theme.accent1}`
              }}>
                <CheckCircle size={22} style={{ color: theme.accent1, flexShrink: 0 }} />
                <span style={{ fontSize: '1rem', color: theme.text }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "steps":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', maxWidth: '800px', margin: '0 auto' }}>
            {slide.items.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '1rem',
                background: 'white', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', textAlign: 'center'
              }}>
                <div style={{
                  width: '40px', height: '40px', borderRadius: '50%', background: theme.primary,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0.5rem',
                  color: 'white', fontWeight: 'bold', fontSize: '1.1rem'
                }}>{item.num}</div>
                <span style={{ fontSize: '0.85rem', color: theme.text }}>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "livret1":
    case "livret2":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ maxWidth: '600px', margin: '0 auto', background: 'white', borderRadius: '1rem', padding: '2rem', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem' }}>
              <FileText size={50} style={{ color: theme.primary }} />
            </div>
            {slide.items.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.75rem',
                borderBottom: idx < slide.items.length - 1 ? `1px solid ${theme.accent3}30` : 'none'
              }}>
                <CheckCircle size={18} style={{ color: theme.accent1, flexShrink: 0 }} />
                <span style={{ fontSize: '1rem', color: theme.text }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "blocs-overview":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '700px', margin: '0 auto' }}>
            {slide.blocs.map((bloc, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem',
                background: bloc.color, borderRadius: '0.75rem', color: 'white'
              }}>
                <div style={{
                  width: '45px', height: '45px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '1.25rem', flexShrink: 0
                }}>{bloc.num}</div>
                <div style={{ flex: 1 }}>
                  <h4 style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>{bloc.title}</h4>
                  <span style={{ fontSize: '0.85rem', opacity: 0.9 }}>Activités {bloc.activities}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      );

    case "bloc-title":
      return (
        <div style={{ 
          textAlign: 'center', 
          padding: '3rem 2rem',
          background: slide.color,
          borderRadius: '1rem',
          color: 'white'
        }}>
          <div style={{ 
            width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem'
          }}>
            {getIcon(slide.icon, 40)}
          </div>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '0.75rem' }}>{slide.title}</h2>
          <p style={{ fontSize: '1.1rem', opacity: 0.9 }}>{slide.subtitle}</p>
        </div>
      );

    case "activity":
      return (
        <div style={{ padding: '1rem' }}>
          <div style={{ 
            background: slide.color, color: 'white', padding: '1rem 1.5rem', 
            borderRadius: '0.75rem 0.75rem 0 0', marginBottom: '0'
          }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>{slide.title}</h2>
            {slide.subtitle && <p style={{ fontSize: '0.95rem', opacity: 0.9 }}>{slide.subtitle}</p>}
          </div>
          <div style={{ 
            background: 'white', borderRadius: '0 0 0.75rem 0.75rem', 
            padding: '1rem', boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            {slide.items.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '0.6rem 0',
                borderBottom: idx < slide.items.length - 1 ? `1px solid #eee` : 'none'
              }}>
                <div style={{
                  width: '6px', height: '6px', borderRadius: '50%', background: slide.color,
                  marginTop: '0.5rem', flexShrink: 0
                }} />
                <span style={{ fontSize: '0.9rem', color: theme.text, lineHeight: '1.4' }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "tips":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxWidth: '600px', margin: '0 auto 1rem' }}>
            {slide.tips.map((tip, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem',
                background: 'white', borderRadius: '0.5rem', boxShadow: '0 2px 6px rgba(0,0,0,0.06)'
              }}>
                <Target size={18} style={{ color: theme.secondary, flexShrink: 0 }} />
                <span style={{ fontSize: '0.95rem', color: theme.text }}>{tip}</span>
              </div>
            ))}
          </div>
          <div style={{
            maxWidth: '600px', margin: '0 auto', padding: '1rem',
            background: '#f8f9fa', borderRadius: '0.75rem', border: `2px solid ${theme.accent3}`
          }}>
            <p style={{ fontWeight: 'bold', marginBottom: '0.75rem', color: theme.primary, textAlign: 'center', fontSize: '0.95rem' }}>
              {slide.example.title}
            </p>
            <div style={{ padding: '0.5rem 0.75rem', background: '#ffebee', borderRadius: '0.5rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ color: theme.error, fontWeight: 'bold' }}>✗</span>
              <span style={{ color: theme.error, fontSize: '0.9rem' }}>{slide.example.wrong}</span>
            </div>
            <div style={{ padding: '0.5rem 0.75rem', background: '#e8f5e9', borderRadius: '0.5rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
              <span style={{ color: theme.success, fontWeight: 'bold' }}>✓</span>
              <span style={{ color: theme.success, fontSize: '0.9rem' }}>{slide.example.right}</span>
            </div>
          </div>
        </div>
      );

    case "accompagnement":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '500px', margin: '0 auto' }}>
            {slide.items.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem',
                background: 'white', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                borderLeft: `4px solid ${theme.accent1}`
              }}>
                <CheckCircle size={22} style={{ color: theme.success, flexShrink: 0 }} />
                <span style={{ fontSize: '1rem', color: theme.text }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "oral":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ maxWidth: '500px', margin: '0 auto', background: 'white', borderRadius: '1rem', padding: '1.5rem', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
            {slide.details.map((detail, idx) => (
              <div key={idx} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem',
                background: theme.background, borderRadius: '0.5rem', marginBottom: '0.75rem'
              }}>
                <span style={{ fontWeight: 'bold', color: theme.text }}>{detail.label}</span>
                <span style={{ color: theme.primary, fontWeight: 'bold' }}>{detail.value}</span>
              </div>
            ))}
            <div style={{ padding: '0.75rem', background: theme.accent3 + '30', borderRadius: '0.5rem' }}>
              <p style={{ fontSize: '0.9rem', color: theme.text, fontStyle: 'italic' }}>{slide.note}</p>
            </div>
          </div>
        </div>
      );

    case "decision":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '500px', margin: '0 auto' }}>
            {slide.decisions.map((decision, idx) => (
              <div key={idx} style={{
                padding: '1rem',
                background: decision.status === 'success' ? '#e8f5e9' : decision.status === 'partial' ? '#fff3e0' : '#ffebee',
                borderRadius: '0.75rem',
                borderLeft: `4px solid ${decision.status === 'success' ? theme.success : decision.status === 'partial' ? '#ff9800' : theme.error}`
              }}>
                <h4 style={{ fontWeight: 'bold', marginBottom: '0.25rem', color: decision.status === 'success' ? theme.success : decision.status === 'partial' ? '#e65100' : theme.error }}>
                  {decision.text}
                </h4>
                <span style={{ fontSize: '0.9rem', color: theme.text }}>{decision.desc}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "timeline":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '500px', margin: '0 auto 1.5rem' }}>
            {slide.phases.map((phase, idx) => (
              <div key={idx} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem',
                background: 'white', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <Clock size={22} style={{ color: theme.primary }} />
                  <span style={{ fontWeight: 'bold', color: theme.text }}>{phase.phase}</span>
                </div>
                <span style={{ fontWeight: 'bold', color: theme.primary }}>{phase.duration}</span>
              </div>
            ))}
          </div>
          <div style={{ padding: '1rem', background: theme.primary, borderRadius: '0.75rem', textAlign: 'center', maxWidth: '500px', margin: '0 auto' }}>
            <span style={{ fontWeight: 'bold', color: 'white', fontSize: '1.1rem' }}>{slide.total}</span>
          </div>
        </div>
      );

    case "careers":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem', maxWidth: '650px', margin: '0 auto' }}>
            {slide.jobs.map((job, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem',
                background: 'white', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}>
                <Baby size={18} style={{ color: theme.primary, flexShrink: 0 }} />
                <span style={{ fontSize: '0.9rem', color: theme.text }}>{job}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "summary":
      return (
        <div style={{ 
          padding: '2rem',
          background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.accent1} 100%)`,
          borderRadius: '1rem',
          textAlign: 'center'
        }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', color: 'white' }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '500px', margin: '0 auto' }}>
            {slide.points.map((point, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', padding: '0.75rem',
                background: 'rgba(255,255,255,0.15)', borderRadius: '0.75rem'
              }}>
                <CheckCircle size={20} style={{ color: 'white', flexShrink: 0 }} />
                <span style={{ color: 'white', fontSize: '1rem' }}>{point}</span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'rgba(255,255,255,0.9)', borderRadius: '0.75rem' }}>
            <p style={{ color: theme.primary, fontWeight: 'bold' }}>
              Lancez-vous dans l'aventure VAE DEAP avec notre accompagnement personnalisé !
            </p>
          </div>
        </div>
      );

    default:
      return null;
  }
};

const DEAPPresentation = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);

  React.useEffect(() => {
    let interval;
    if (isPlaying) {
      interval = setInterval(() => {
        setCurrentSlide((prev) => {
          if (prev === slides.length - 1) { setIsPlaying(false); return prev; }
          return prev + 1;
        });
      }, 6000);
    }
    return () => clearInterval(interval);
  }, [isPlaying]);

  return (
    <div style={{ borderRadius: '1rem', overflow: 'hidden', background: theme.background, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }} data-testid="deap-presentation">
      <div style={{ minHeight: '450px', padding: '1.5rem', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <SlideContent slide={slides[currentSlide]} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1.5rem', borderTop: `1px solid ${theme.accent3}30`, background: 'white' }}>
        <button onClick={prevSlide} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1rem', border: `1px solid ${theme.accent3}`, borderRadius: '0.5rem', background: 'white', cursor: 'pointer', color: theme.text, fontWeight: '500' }} data-testid="prev-slide-btn">
          <ChevronLeft size={18} /><span>Précédent</span>
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button onClick={() => setIsPlaying(!isPlaying)} style={{ width: '40px', height: '40px', borderRadius: '50%', border: 'none', background: theme.primary, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }} data-testid="play-pause-btn">
            {isPlaying ? '⏸' : <Play size={16} />}
          </button>
          <span style={{ fontWeight: 'bold', color: theme.primary, fontSize: '0.95rem' }}>{currentSlide + 1} / {slides.length}</span>
        </div>
        <button onClick={nextSlide} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1rem', border: 'none', borderRadius: '0.5rem', background: theme.primary, cursor: 'pointer', color: 'white', fontWeight: '500' }} data-testid="next-slide-btn">
          <span>Suivant</span><ChevronRight size={18} />
        </button>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: '0.3rem', padding: '0.75rem', background: 'white', flexWrap: 'wrap' }}>
        {slides.map((_, idx) => (
          <button key={idx} onClick={() => setCurrentSlide(idx)} style={{ width: '8px', height: '8px', borderRadius: '50%', border: 'none', background: idx === currentSlide ? theme.primary : theme.accent3, cursor: 'pointer', opacity: idx === currentSlide ? 1 : 0.4 }} data-testid={`slide-dot-${idx}`} />
        ))}
      </div>
    </div>
  );
};

export default DEAPPresentation;
